import { translate } from "@/i18n";
import { libChatMessages } from "@/i18n/messages/lib-chat";

/** Context occupancy is separate from cumulative billing usage. */
export function contextUsagePercent({
  used,
  max,
}: {
  used?: number | null;
  input?: number | null;
  output?: number | null;
  max?: number | null;
  fallbackPercent?: number | null;
}): number | null {
  const valid = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value) && value >= 0;
  // Billing totals and persisted percentages are not native occupancy.
  return valid(used) && valid(max) && max > 0
    ? Math.min(100, (used / max) * 100)
    : null;
}

/** Compact token count, e.g. 7, 1.2k, 31k, 1.0m. */
export function formatContextTokens(tokens: number): string {
  if (tokens < 1_000) return `${tokens}`;
  if (tokens < 1_000_000) {
    return `${(tokens / 1_000).toFixed(tokens < 10_000 ? 1 : 0)}k`;
  }
  return `${(tokens / 1_000_000).toFixed(1)}m`;
}

/** Measured occupancy over the context window, e.g. "31k / 200k tokens (16%)". */
export function formatContextUsage(used: number, max: number): string {
  const rounded = Math.round(contextUsagePercent({ used, max }) ?? 0);
  const percent = used > 0 && rounded === 0 ? "<1" : `${rounded}`;
  return translate(libChatMessages, "contextUsage", {
    used: formatContextTokens(used),
    max: formatContextTokens(max),
    percent,
  });
}
